import React from 'react';
import Tippy from '@tippyjs/react/headless';
import moment from 'moment';
import { Calendar, CalendarProps } from '../Calendar/Calendar';
import { Box } from '../../Box/Box';
import { Card } from '../../Card/Card';
import { Input } from '../../Input/Input';
import { Button } from '../../Button/Button';
import { useSingleDatePicker } from './useSingleDatePicker';
import { DATE_FORMAT } from '../utils';

export type FormVariant = 'success' | 'warning' | 'error';

interface SingleDatePickerProps extends Omit<CalendarProps, 'onChange'> {
  date?: Date;
  onChange(date: Date): void;
  isWrapperWidth?: boolean;
  disabled?: boolean;
  formVariant?: FormVariant;
  id?: string;
}

export const SingleDatePicker: React.FC<SingleDatePickerProps> = ({
  date,
  minDate,
  maxDate,
  onChange,
  isWrapperWidth = false,
  disabled,
  formVariant,
  id = '',
  ...rest
}) => {
  const { state, refs, handlers, helpers } = useSingleDatePicker({
    date,
    maxDate,
    minDate,
    onChange,
  });

  return (
    <>
      {/* Hidden input added to associate with label and resolve "Orphaned Form Label" accessibility alert.  */}
      <input type="hidden" id={id} />
      <Tippy
        interactive
        visible={state.isOpen}
        placement="bottom-start"
        onMount={() => {
          if (refs.inputRef.current) {
            refs.inputRef.current.focus();
          }

          document.addEventListener('keydown', handlers.handleEsc);
        }}
        onHide={() => {
          document.removeEventListener('keydown', handlers.handleEsc);
        }}
        onClickOutside={() => {
          state.setInternalDate(date);

          handlers.close();
        }}
        appendTo={document.body}
        zIndex={20000}
        offset={[0, 4]}
        render={attrs => (
          <Card {...attrs} hasBorder boxShadow="500" width={296} p="24">
            <Box display="flex" justifyContent="center" mb="12">
              <Box width={110}>
                <Input
                  ref={refs.inputRef}
                  type="text"
                  value={state.inputValue}
                  onChange={handlers.onDateChange}
                  onBlur={helpers.validateDate}
                  onKeyDown={handlers.onInputKeydown}
                  placeholder={DATE_FORMAT}
                  textAlign="center"
                  disabled={disabled}
                  aria-label="Date"
                />
              </Box>
            </Box>

            <Calendar
              value={state.internalDate}
              onChange={_date => handlers.onInternalChange(_date as Date)}
              minDate={minDate}
              maxDate={maxDate}
              {...rest}
            />

            <Box display="flex" flexDirection="row" justifyContent="flex-end" mt="24">
              <Button
                variant="subtleInactive"
                onClick={() => {
                  state.setInternalDate(date);

                  handlers.close();
                }}
                disabled={disabled}
              >
                Cancel
              </Button>

              <Button ref={refs.submitRef} onClick={handlers.commit} ml="8">
                Apply
              </Button>
            </Box>
          </Card>
        )}
      >
        <Button
          ref={refs.triggerRef}
          variant={formVariant === 'error' ? 'errorSecondary' : 'secondary'}
          leftIcon="far fa-calendar-alt"
          onClick={() => (state.isOpen ? handlers.close() : handlers.open())}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') {
              handlers.open();
            }
          }}
          css={
            isWrapperWidth
              ? 'font-weight: 400; width: 100%; justify-content: flex-start'
              : 'font-weight: 400'
          }
          disabled={disabled}
        >
          {date ? moment(date).format(DATE_FORMAT) : 'Select Date'}
        </Button>
      </Tippy>
    </>
  );
};
